<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FilterBar from '@lib/components/shared/FilterBar.vue'
import BInput from '@lib/components/base/BInput.vue'
import BSelect from '@lib/components/base/BSelect.vue'
import BDateTimeInput from '@lib/components/base/BDateTimeInput.vue'
import type { EventFilters } from '@/composables/useEventsData'

const props = defineProps<{ modelValue: EventFilters }>()
const emit = defineEmits<{
    (e: 'update:modelValue', v: EventFilters): void
    (e: 'clear'): void
}>()

const { t } = useI18n()

const statusOptions = computed(() => [
    { value: 'all', label: t('events.filter.statusOptions.all') },
    { value: 'upcoming', label: t('events.filter.statusOptions.upcoming') },
    { value: 'ongoing', label: t('events.filter.statusOptions.ongoing') },
    { value: 'past', label: t('events.filter.statusOptions.past') }
])

const activeCount = computed(() => {
    let n = 0
    if (props.modelValue.search) n++
    if (props.modelValue.status !== 'all') n++
    if (props.modelValue.from) n++
    if (props.modelValue.to) n++
    return n
})

function update<K extends keyof EventFilters>(key: K, value: EventFilters[K]) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}
</script>

<template>
    <FilterBar :active-count="activeCount" @clear="$emit('clear')">
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('events.filter.search') }}</span>
            <BInput
                :model-value="modelValue.search"
                :placeholder="t('events.filter.search')"
                @update:model-value="v => update('search', v)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('events.filter.status') }}</span>
            <BSelect
                :model-value="modelValue.status"
                :options="statusOptions"
                option-label="label"
                option-value="value"
                :placeholder="t('events.filter.status')"
                :show-clear="false"
                @update:model-value="v => update('status', v as EventFilters['status'])"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('events.filter.from') }}</span>
            <BDateTimeInput
                :model-value="modelValue.from"
                :show-time="false"
                :placeholder="t('events.filter.from')"
                @update:model-value="v => update('from', v)"
            />
        </label>
        <label class="block">
            <span class="text-xs text-gray-600 mb-1 block">{{ t('events.filter.to') }}</span>
            <BDateTimeInput
                :model-value="modelValue.to"
                :show-time="false"
                :placeholder="t('events.filter.to')"
                @update:model-value="v => update('to', v)"
            />
        </label>
    </FilterBar>
</template>
